import React, { useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  DragStart,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { Shippings } from '@app/pages/checkout-app/types';
import { DeliveryMethodSectionBlock } from './DeliveryMethodSectionBlock';
import {
  DeliveryMethodSectionMethodRowsDroppable,
  METHOD_DRAGGABLE_ID_PREFIX,
  METHOD_DRAG_TYPE,
} from './DeliveryMethodSectionMethodRowsDroppable';
import { InlineSectionNameInput } from './InlineSectionNameInput';
import {
  DeliveryMethodDisplayOrder,
  UNSECTIONED_SECTION_KEY,
  addSection,
  convertVisibleDestinationIndexToSectionIndex,
  createDeliveryMethodSectionId,
  deleteSection,
  moveDeliveryMethods,
  renameSection,
  reorderSections,
} from './deliveryMethodDisplayOrder';

const SECTION_DRAG_TYPE = 'SECTION';
const SECTIONS_DROPPABLE_ID = 'delivery-method-sections';
const SECTION_DRAGGABLE_ID_PREFIX = 'section:';

interface DeliveryMethodSectionsProps {
  displayOrder: DeliveryMethodDisplayOrder;
  deliveryMethodById: Map<string, Shippings>;
  isMethodVisible: (deliveryMethod: Shippings) => boolean;
  isFilterActive: boolean;
  selectedMethodIds: Set<string>;
  isCreatingSection: boolean;
  onFinishCreatingSection: () => void;
  collapsedSectionKeys: Set<string>;
  onToggleSectionCollapsed: (sectionKey: string) => void;
  applyDisplayOrderChange: (
    changeDisplayOrder: (currentDisplayOrder: DeliveryMethodDisplayOrder) => DeliveryMethodDisplayOrder,
  ) => void;
  renderMethodRow: (
    deliveryMethod: Shippings,
    dragHandleProps?: DraggableProvidedDragHandleProps | null,
  ) => React.ReactNode;
}

export const DeliveryMethodSections = ({
  displayOrder,
  deliveryMethodById,
  isMethodVisible,
  isFilterActive,
  selectedMethodIds,
  isCreatingSection,
  onFinishCreatingSection,
  collapsedSectionKeys,
  onToggleSectionCollapsed,
  applyDisplayOrderChange,
  renderMethodRow,
}: DeliveryMethodSectionsProps) => {
  const [draggingMethodId, setDraggingMethodId] = useState<string | null>(null);
  const isSectionDragEnabled = !isFilterActive;
  const selectedMethodIdStrings = new Set([...selectedMethodIds].map(String));

  const getMovingMethodIds = (draggedMethodId: string): string[] =>
    selectedMethodIdStrings.has(draggedMethodId)
      ? [...selectedMethodIdStrings].filter((methodId) => deliveryMethodById.has(methodId))
      : [draggedMethodId];

  const handleDragStart = (dragStart: DragStart) => {
    if (dragStart.type === METHOD_DRAG_TYPE) {
      setDraggingMethodId(dragStart.draggableId.slice(METHOD_DRAGGABLE_ID_PREFIX.length));
    }
  };

  const handleDragEnd = ({ type, source, destination, draggableId }: DropResult) => {
    setDraggingMethodId(null);
    if (!destination) {
      return;
    }
    if (type === SECTION_DRAG_TYPE) {
      if (source.index !== destination.index) {
        applyDisplayOrderChange((currentDisplayOrder) =>
          reorderSections(currentDisplayOrder, source.index, destination.index),
        );
      }
      return;
    }
    const draggedMethodId = draggableId.slice(METHOD_DRAGGABLE_ID_PREFIX.length);
    const movingMethodIds = getMovingMethodIds(draggedMethodId);
    const isUnchangedSingleMove =
      movingMethodIds.length === 1 &&
      source.droppableId === destination.droppableId &&
      source.index === destination.index;
    if (isUnchangedSingleMove) {
      return;
    }
    const destinationSectionIndex = convertVisibleDestinationIndexToSectionIndex(
      displayOrder.methodIdsBySectionKey[destination.droppableId] ?? [],
      getVisibleMethodIds(destination.droppableId),
      draggedMethodId,
      destination.index,
    );
    applyDisplayOrderChange((currentDisplayOrder) =>
      moveDeliveryMethods(
        currentDisplayOrder,
        draggedMethodId,
        movingMethodIds,
        destination.droppableId,
        destinationSectionIndex,
      ),
    );
  };

  const getVisibleMethodIds = (sectionKey: string) =>
    (displayOrder.methodIdsBySectionKey[sectionKey] ?? []).filter((methodId) => {
      const deliveryMethod = deliveryMethodById.get(methodId);
      return deliveryMethod && isMethodVisible(deliveryMethod);
    });

  const getDeliveryMethodCountLabel = (sectionKey: string) => {
    const totalMethodCount = displayOrder.methodIdsBySectionKey[sectionKey]?.length ?? 0;
    return isFilterActive
      ? `${getVisibleMethodIds(sectionKey).length}/${totalMethodCount} delivery methods`
      : `${totalMethodCount} delivery methods`;
  };

  const renderSectionMethodRows = (sectionKey: string, showEmptyState: boolean) => (
    <DeliveryMethodSectionMethodRowsDroppable
      sectionKey={sectionKey}
      showEmptyState={showEmptyState}
      visibleMethodIds={getVisibleMethodIds(sectionKey)}
      deliveryMethodById={deliveryMethodById}
      selectedMethodIdStrings={selectedMethodIdStrings}
      draggingMethodId={draggingMethodId}
      movingMethodCount={draggingMethodId ? getMovingMethodIds(draggingMethodId).length : 0}
      renderMethodRow={renderMethodRow}
    />
  );

  const renderSectionBlock = (
    sectionKey: string,
    sectionName: string,
    sectionHeaderControls: Pick<
      React.ComponentProps<typeof DeliveryMethodSectionBlock>,
      'dragHandleProps' | 'onRenameSection' | 'onDeleteSection'
    > = {},
  ) => (
    <DeliveryMethodSectionBlock
      sectionName={sectionName}
      deliveryMethodCountLabel={getDeliveryMethodCountLabel(sectionKey)}
      isCollapsed={!isFilterActive && collapsedSectionKeys.has(sectionKey)}
      canCollapse={!isFilterActive}
      hasNoMatchesUnderFilter={isFilterActive && getVisibleMethodIds(sectionKey).length === 0}
      onToggleCollapsed={() => onToggleSectionCollapsed(sectionKey)}
      {...sectionHeaderControls}>
      {renderSectionMethodRows(sectionKey, true)}
    </DeliveryMethodSectionBlock>
  );

  const getSectionRenameAndDeleteHandlers = (sectionId: string) => ({
    onRenameSection: (sectionName: string) =>
      applyDisplayOrderChange((currentDisplayOrder) => renameSection(currentDisplayOrder, sectionId, sectionName)),
    onDeleteSection: () =>
      applyDisplayOrderChange((currentDisplayOrder) => deleteSection(currentDisplayOrder, sectionId)),
  });

  const hasSections = displayOrder.sections.length > 0;
  const sectionsWithoutFilterMatches = isFilterActive
    ? displayOrder.sections.filter((section) => getVisibleMethodIds(section.id).length === 0)
    : [];
  const sectionsInDisplayOrder = displayOrder.sections.filter(
    (section) => !sectionsWithoutFilterMatches.includes(section),
  );

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="delivery-method-sections">
        {isCreatingSection && (
          <div className="delivery-method-sections__section">
            <div className="delivery-method-sections__header">
              <InlineSectionNameInput
                initialName=""
                onSubmitName={(sectionName) => {
                  applyDisplayOrderChange((currentDisplayOrder) =>
                    addSection(currentDisplayOrder, { id: createDeliveryMethodSectionId(), name: sectionName }),
                  );
                  onFinishCreatingSection();
                }}
                onCancel={onFinishCreatingSection}
              />
            </div>
          </div>
        )}
        <Droppable droppableId={SECTIONS_DROPPABLE_ID} type={SECTION_DRAG_TYPE} isDropDisabled={!isSectionDragEnabled}>
          {(sectionsDroppableProvided) => (
            <div
              ref={sectionsDroppableProvided.innerRef}
              {...sectionsDroppableProvided.droppableProps}
              className="delivery-method-sections__list">
              {sectionsInDisplayOrder.map((section, sectionIndex) => (
                <Draggable
                  key={section.id}
                  draggableId={`${SECTION_DRAGGABLE_ID_PREFIX}${section.id}`}
                  index={sectionIndex}
                  isDragDisabled={!isSectionDragEnabled}>
                  {(sectionDraggableProvided, sectionDraggableSnapshot) => (
                    <div
                      ref={sectionDraggableProvided.innerRef}
                      {...sectionDraggableProvided.draggableProps}
                      className={`delivery-method-sections__section ${
                        sectionDraggableSnapshot.isDragging ? 'delivery-method-sections__section--dragging' : ''
                      }`}>
                      {renderSectionBlock(section.id, section.name, {
                        dragHandleProps: isSectionDragEnabled ? sectionDraggableProvided.dragHandleProps : null,
                        ...getSectionRenameAndDeleteHandlers(section.id),
                      })}
                    </div>
                  )}
                </Draggable>
              ))}
              {sectionsDroppableProvided.placeholder}
            </div>
          )}
        </Droppable>
        {hasSections ? (
          <div className="delivery-method-sections__section">
            {renderSectionBlock(UNSECTIONED_SECTION_KEY, 'Unsectioned')}
          </div>
        ) : (
          <div className="delivery-method-sections__section">
            {renderSectionMethodRows(UNSECTIONED_SECTION_KEY, false)}
          </div>
        )}
        {sectionsWithoutFilterMatches.map((section) => (
          <div key={section.id} className="delivery-method-sections__section">
            {renderSectionBlock(section.id, section.name, getSectionRenameAndDeleteHandlers(section.id))}
          </div>
        ))}
      </div>
    </DragDropContext>
  );
};
